import { Currency } from "@/components/ui/currency";
import { cn } from "@/utils/cn";

const PRICE_SIZE_CLASSES = {
  body: "text-body",
  "body-lg": "text-body-lg",
  h3: "text-h3",
} as const;

export interface PriceProps {
  value: number;
  /** Present and greater than `value` → renders as a struck-through
   * secondary amount (component-inventory.md `ProductCard`/
   * `WishlistProductCard`'s discount-strikethrough variant). */
  originalValue?: number;
  currency?: string;
  size?: keyof typeof PRICE_SIZE_CLASSES;
  className?: string;
}

/**
 * docs/ui/design-system.md §3: "Price display gets its own weight
 * regardless of context: 600 weight, Text Primary, never muted — it's the
 * single most-scanned piece of text on a ProductCard." That rule is
 * hardcoded here (`font-semibold text-foreground`), not left for each call
 * site to remember.
 */
export function Price({ value, originalValue, currency, size = "body-lg", className }: PriceProps) {
  const hasDiscount = originalValue != null && originalValue > value;

  return (
    <span className={cn("inline-flex items-baseline gap-2", className)}>
      <span className={cn(PRICE_SIZE_CLASSES[size], "font-semibold text-foreground")}>
        <Currency value={value} currency={currency} />
      </span>
      {hasDiscount && (
        <span className="text-body-sm text-muted-foreground line-through">
          <Currency value={originalValue} currency={currency} />
        </span>
      )}
    </span>
  );
}
